import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiSearch, FiChevronRight } from 'react-icons/fi';

import { useAdminApi } from '../../../Admin/hooks/useAdminApi';
import {
  useAdminResource,
  PageHeader,
  ErrorState,
  SkeletonTable,
} from '../../../../../components/Admin';
import {
  AdminCourse,
  isCourseActive,
  getCourseStatus,
} from '../../../../../models/CourseModels';

import {
  PickerList,
  PickerItem,
  PickerThumb,
  PickerInfo,
  PickerTitle,
  PickerMeta,
  SearchBox,
  SearchInput,
  EmptyHint,
} from '../../styles';

const STATUS_LABEL = {
  active: 'Ativo',
  soon: 'Em breve',
  inactive: 'Inativo',
} as const;

/**
 * Seleção de curso para ver as vendas. Evita a tela vazia: o usuário escolhe
 * o curso aqui mesmo, com busca, em vez de precisar voltar para "Cursos".
 */
const CoursePicker: React.FC = () => {
  const { push } = useHistory();
  const { fetchCourses } = useAdminApi();
  const [search, setSearch] = useState('');

  const { data, isLoading, loadError, reload } = useAdminResource<AdminCourse, {
    pages: number;
    courses: AdminCourse[];
  }>({
    fetchPage: (page) => fetchCourses(page),
    select: (response) => response.courses ?? [],
    selectPages: (response) => response.pages ?? 0,
    filter: isCourseActive,
    errorMessage: 'Não foi possível carregar os cursos.',
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    if (!q) return data;
    return data.filter((course) => course.title.toLocaleLowerCase().includes(q));
  }, [data, search]);

  return (
    <>
      <PageHeader
        title="Vendas por curso"
        subtitle="Escolha um curso para ver as vendas."
      />

      {isLoading && data.length === 0 ? (
        <SkeletonTable rows={5} columns={2} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={reload} />
      ) : (
        <>
          <SearchBox>
            <FiSearch size={16} aria-hidden />
            <SearchInput
              type="search"
              value={search}
              placeholder="Buscar curso por título..."
              aria-label="Buscar curso por título"
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBox>

          {filtered.length === 0 ? (
            <EmptyHint>
              {data.length === 0
                ? 'Nenhum curso cadastrado ainda.'
                : 'Nenhum curso encontrado para essa busca.'}
            </EmptyHint>
          ) : (
            <PickerList>
              {filtered.map((course) => (
                <PickerItem
                  key={course.courseid}
                  type="button"
                  onClick={() => push(`/admin/sales?courseid=${course.courseid}`)}
                >
                  <PickerThumb src={course.thumburl} alt="" />
                  <PickerInfo>
                    <PickerTitle>{course.title}</PickerTitle>
                    <PickerMeta>
                      {STATUS_LABEL[getCourseStatus(course)]} ·{' '}
                      {course.comingsoon === 'true'
                        ? 'Em breve'
                        : `R$ ${course.price.toFixed(2)}`}
                    </PickerMeta>
                  </PickerInfo>
                  <FiChevronRight size={18} aria-hidden />
                </PickerItem>
              ))}
            </PickerList>
          )}
        </>
      )}
    </>
  );
};

export default CoursePicker;
